import { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  MessageSquare, Send, Loader2, User, Bot, Trophy, Target,
  ChevronRight, BarChart3, Clock, Zap, ArrowLeft, History,
  Crosshair, BrainCircuit, AlertTriangle, CheckCircle2, X,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { PageHint, UpgradeNudge } from '../components/ActivationWidgets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GRADE_STYLES = {
  A: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', label: 'Excellent' },
  B: { color: 'text-sky-400', bg: 'bg-sky-500/10', border: 'border-sky-500/30', label: 'Good' },
  C: { color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30', label: 'Average' },
  D: { color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30', label: 'Needs Work' },
  F: { color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', label: 'Poor' },
};

const DIFF_STYLES = {
  easy: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
  medium: { color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
  hard: { color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20' },
};

function ScoreBar({ label, value, icon: Icon }) {
  const barColor = value >= 75 ? 'bg-emerald-500' : value >= 50 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="space-y-1" data-testid={`score-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {Icon && <Icon size={12} className="text-muted-foreground" />}
          <span className="text-xs text-muted-foreground">{label}</span>
        </div>
        <span className="text-xs font-medium text-foreground">{value}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div className={`h-full rounded-full ${barColor} transition-all duration-700`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

function MessageBubble({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex gap-2.5 ${isUser ? 'flex-row-reverse' : ''}`} data-testid={`msg-${msg.role}`}>
      <div className={`shrink-0 h-7 w-7 rounded-full flex items-center justify-center ${isUser ? 'bg-sky-500/20' : 'bg-emerald-500/20'}`}>
        {isUser ? <User size={14} className="text-sky-400" /> : <Bot size={14} className="text-emerald-400" />}
      </div>
      <div className={`max-w-[80%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed ${
        isUser
          ? 'bg-sky-500/15 text-sky-50 rounded-tr-sm'
          : 'bg-muted/80 text-foreground rounded-tl-sm border border-border'
      }`}>
        {msg.content}
      </div>
    </div>
  );
}

function Scorecard({ scorecard, onClose }) {
  if (!scorecard) return null;
  const grade = GRADE_STYLES[scorecard.grade] || GRADE_STYLES.C;

  return (
    <div className="space-y-4 animate-in fade-in duration-300" data-testid="scorecard">
      {/* Grade + Overall */}
      <div className="flex items-center gap-4">
        <div className={`h-20 w-20 rounded-2xl ${grade.bg} ${grade.border} border-2 flex flex-col items-center justify-center`}>
          <span className={`text-3xl font-bold ${grade.color}`}>{scorecard.grade}</span>
          <span className={`text-[9px] font-medium ${grade.color} opacity-80`}>{grade.label}</span>
        </div>
        <div className="flex-1 space-y-1">
          <div className="flex items-center gap-2">
            <Trophy size={16} className={grade.color} />
            <span className="text-lg font-semibold text-foreground">Conversion Score: {scorecard.scores?.overall}%</span>
          </div>
          <p className="text-xs text-muted-foreground">
            {scorecard.total_turns} turns | {scorecard.entities_count} entities captured | {scorecard.tool_calls_count} tool calls
          </p>
        </div>
        {onClose && (
          <button onClick={onClose} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors" data-testid="close-scorecard">
            <X size={16} />
          </button>
        )}
      </div>

      {/* Score Breakdown */}
      <Card className="border-border bg-card">
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-foreground text-xs uppercase tracking-wider">Score Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 px-4 pb-4">
          <ScoreBar label="Lead Capture" value={scorecard.scores?.lead_capture || 0} icon={User} />
          <ScoreBar label="Engagement" value={scorecard.scores?.engagement || 0} icon={MessageSquare} />
          <ScoreBar label="Intent Detection" value={scorecard.scores?.intent_detection || 0} icon={Crosshair} />
          <ScoreBar label="Conversion Action" value={scorecard.scores?.conversion_action || 0} icon={Target} />
          <ScoreBar label="Objection Handling" value={scorecard.scores?.objection_handling || 0} icon={AlertTriangle} />
        </CardContent>
      </Card>

      {/* Suggestions */}
      {scorecard.suggestions?.length > 0 && (
        <Card className="border-border bg-card">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-foreground text-xs uppercase tracking-wider flex items-center gap-1.5">
              <BrainCircuit size={12} className="text-violet-400" /> Improvement Tips
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 px-4 pb-4">
            {scorecard.suggestions.map((s, i) => (
              <div key={i} className="flex gap-2 text-xs" data-testid={`suggestion-${i}`}>
                <ChevronRight size={12} className="text-violet-400 shrink-0 mt-0.5" />
                <span className="text-foreground">{s}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Metadata */}
      <div className="flex flex-wrap gap-1.5">
        {scorecard.intents_detected?.map(i => (
          <Badge key={i} className="text-[9px] bg-violet-500/10 text-violet-400 border-violet-500/20">{i.replace(/_/g, ' ')}</Badge>
        ))}
        {scorecard.tool_calls?.map((t, idx) => (
          <Badge key={idx} className="text-[9px] bg-cyan-500/10 text-cyan-400 border-cyan-500/20">{t}</Badge>
        ))}
      </div>
    </div>
  );
}

export default function CallSimulationPage() {
  const { token } = useAuth();
  const [config, setConfig] = useState(null);
  const [scenario, setScenario] = useState('first_time_buyer');
  const [difficulty, setDifficulty] = useState('medium');
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(false);
  const [ending, setEnding] = useState(false);
  const [scorecard, setScorecard] = useState(null);
  const [pastSessions, setPastSessions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [viewingSession, setViewingSession] = useState(null);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  // Load config + sessions on mount
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/simulation/config`, { headers }),
      axios.get(`${API}/simulation/sessions`, { headers }),
    ]).then(([configRes, sessionsRes]) => {
      setConfig(configRes.data);
      setPastSessions(sessionsRes.data?.sessions || []);
    }).catch(() => toast.error('Failed to load simulation config'));
  }, [token]);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const startSession = async () => {
    setStarting(true);
    setScorecard(null);
    setViewingSession(null);
    try {
      const { data } = await axios.post(`${API}/simulation/start`, { scenario, difficulty }, { headers });
      setSessionId(data.session_id);
      setMessages([{ role: 'assistant', content: data.greeting }]);
      toast.success('Simulation started');
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start simulation');
    } finally {
      setStarting(false);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || sending || !sessionId) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/simulation/chat`, {
        session_id: sessionId,
        message: userMsg,
        scenario,
        difficulty,
      }, { headers });
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (err) {
      toast.error('Failed to get AI response');
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, there was an error processing your message.' }]);
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const endSession = async () => {
    if (!sessionId || ending) return;
    setEnding(true);
    try {
      const { data } = await axios.post(`${API}/simulation/end`, { session_id: sessionId }, { headers });
      setScorecard(data.scorecard);
      setSessionId(null);
      toast.success(`Simulation complete — Grade: ${data.scorecard?.grade}`);
      // Refresh sessions list
      const sessRes = await axios.get(`${API}/simulation/sessions`, { headers });
      setPastSessions(sessRes.data?.sessions || []);
    } catch (err) {
      toast.error('Failed to end simulation');
    } finally {
      setEnding(false);
    }
  };

  const viewSession = async (sid) => {
    try {
      const { data } = await axios.get(`${API}/simulation/sessions/${sid}`, { headers });
      setViewingSession(data);
      setMessages(data.messages || []);
      setScorecard(data.scorecard || null);
      setSessionId(null);
      setShowHistory(false);
    } catch {
      toast.error('Failed to load session');
    }
  };

  const resetToSetup = () => {
    setSessionId(null);
    setMessages([]);
    setScorecard(null);
    setViewingSession(null);
  };

  const isActive = !!sessionId;
  const isReview = !!viewingSession;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto" data-testid="call-simulation-page">
      <PageHint id="simulation-intro" text="Practice with AI-generated callers. Your AI uses the exact same logic as live calls — train it and improve your conversion score." type="tip" />
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">AI Training Simulator</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Practice conversations using the same AI brain as live calls</p>
        </div>
        <div className="flex items-center gap-2">
          {(isActive || isReview) && (
            <Button variant="outline" size="sm" onClick={resetToSetup}
              className="border-border text-foreground hover:text-foreground hover:bg-muted"
              data-testid="back-to-setup-btn">
              <ArrowLeft size={14} className="mr-1.5" /> New Session
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setShowHistory(!showHistory)}
            className="border-border text-foreground hover:text-foreground hover:bg-muted"
            data-testid="toggle-history-btn">
            <History size={14} className="mr-1.5" /> History
            {pastSessions.length > 0 && (
              <Badge className="ml-1.5 text-[9px] bg-accent text-foreground">{pastSessions.length}</Badge>
            )}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* History Sidebar (conditional) */}
        {showHistory && (
          <div className="lg:col-span-3 space-y-2" data-testid="history-panel">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-1">Past Sessions</p>
            {pastSessions.length === 0 && (
              <p className="text-xs text-muted-foreground px-1">No sessions yet</p>
            )}
            {pastSessions.map(s => {
              const g = GRADE_STYLES[s.scorecard?.grade] || GRADE_STYLES.C;
              const d = DIFF_STYLES[s.difficulty] || DIFF_STYLES.medium;
              return (
                <button key={s.session_id} onClick={() => viewSession(s.session_id)}
                  className={`w-full text-left rounded-lg border p-2.5 transition-all ${
                    viewingSession?.session_id === s.session_id
                      ? 'border-emerald-500/40 bg-emerald-500/5'
                      : 'border-border bg-card/40 hover:bg-muted hover:border-border'
                  }`}
                  data-testid={`session-${s.session_id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-foreground truncate">{s.scenario_name}</span>
                    {s.scorecard?.grade && (
                      <span className={`text-xs font-bold ${g.color}`}>{s.scorecard.grade}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Badge className={`text-[8px] ${d.bg} ${d.color} ${d.border}`}>{s.difficulty_name}</Badge>
                    <span className="text-[10px] text-muted-foreground">
                      {new Date(s.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Main Area */}
        <div className={showHistory ? 'lg:col-span-9' : 'lg:col-span-12'}>
          {/* Setup Screen */}
          {!isActive && !isReview && !scorecard && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="setup-panel">
              {/* Scenario Selection */}
              <Card className="border-border bg-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-foreground text-sm flex items-center gap-1.5">
                    <MessageSquare size={14} className="text-emerald-400" /> Choose Scenario
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {config?.scenarios && Object.entries(config.scenarios).map(([key, s]) => (
                    <button key={key} onClick={() => setScenario(key)}
                      className={`w-full text-left rounded-lg border p-3 transition-all ${
                        scenario === key
                          ? 'border-emerald-500/40 bg-emerald-500/5'
                          : 'border-border bg-muted hover:bg-muted hover:border-border'
                      }`}
                      data-testid={`scenario-${key}`}>
                      <p className={`text-sm font-medium ${scenario === key ? 'text-emerald-400' : 'text-foreground'}`}>{s.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{s.description}</p>
                      <Badge className="mt-1.5 text-[8px] bg-accent/50 text-muted-foreground border-border">{s.category}</Badge>
                    </button>
                  ))}
                </CardContent>
              </Card>

              {/* Difficulty + Start */}
              <div className="space-y-4">
                <Card className="border-border bg-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-foreground text-sm flex items-center gap-1.5">
                      <Zap size={14} className="text-amber-400" /> Difficulty
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {config?.difficulties && Object.entries(config.difficulties).map(([key, d]) => {
                      const style = DIFF_STYLES[key] || DIFF_STYLES.medium;
                      return (
                        <button key={key} onClick={() => setDifficulty(key)}
                          className={`w-full text-left rounded-lg border p-3 transition-all ${
                            difficulty === key
                              ? `${style.border} ${style.bg}`
                              : 'border-border bg-muted hover:bg-muted hover:border-border'
                          }`}
                          data-testid={`difficulty-${key}`}>
                          <p className={`text-sm font-medium ${difficulty === key ? style.color : 'text-foreground'}`}>{d.name}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{d.description}</p>
                        </button>
                      );
                    })}
                  </CardContent>
                </Card>

                <Button onClick={startSession} disabled={starting}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-12 text-base"
                  data-testid="start-simulation-btn">
                  {starting ? <><Loader2 size={16} className="animate-spin mr-2" /> Starting...</>
                    : <><MessageSquare size={16} className="mr-2" /> Start Simulation</>}
                </Button>

                {/* Quick stats */}
                {pastSessions.length > 0 && (
                  <Card className="border-border bg-card">
                    <CardContent className="py-3 px-4">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Sessions completed</span>
                        <span className="text-foreground font-medium">{pastSessions.filter(s => s.status === 'completed').length}</span>
                      </div>
                      {pastSessions.some(s => s.scorecard?.grade) && (
                        <div className="flex items-center justify-between text-xs mt-1">
                          <span className="text-muted-foreground">Best grade</span>
                          <span className={`font-bold ${GRADE_STYLES[pastSessions.reduce((best, s) => {
                            const order = ['A', 'B', 'C', 'D', 'F'];
                            const g = s.scorecard?.grade || 'F';
                            return order.indexOf(g) < order.indexOf(best) ? g : best;
                          }, 'F')]?.color || 'text-foreground'}`}>
                            {pastSessions.reduce((best, s) => {
                              const order = ['A', 'B', 'C', 'D', 'F'];
                              const g = s.scorecard?.grade || 'F';
                              return order.indexOf(g) < order.indexOf(best) ? g : best;
                            }, 'F')}
                          </span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}

          {/* Chat Interface (Active Session or Review) */}
          {(isActive || isReview) && (
            <Card className="border-border bg-card" data-testid="chat-panel">
              {/* Chat Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <div className="flex items-center gap-2">
                  <div className={`h-2 w-2 rounded-full ${isActive ? 'bg-emerald-400 animate-pulse' : 'bg-muted-foreground'}`} />
                  <span className="text-sm font-medium text-foreground">
                    {isReview ? viewingSession?.scenario_name : config?.scenarios?.[scenario]?.name}
                  </span>
                  <Badge className={`text-[9px] ${DIFF_STYLES[isReview ? viewingSession?.difficulty : difficulty]?.bg} ${DIFF_STYLES[isReview ? viewingSession?.difficulty : difficulty]?.color} ${DIFF_STYLES[isReview ? viewingSession?.difficulty : difficulty]?.border}`}>
                    {isReview ? viewingSession?.difficulty_name : config?.difficulties?.[difficulty]?.name}
                  </Badge>
                </div>
                {isActive && (
                  <Button variant="outline" size="sm" onClick={endSession} disabled={ending || messages.length < 3}
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                    data-testid="end-simulation-btn">
                    {ending ? <Loader2 size={12} className="animate-spin mr-1" /> : <Target size={12} className="mr-1" />}
                    End & Score
                  </Button>
                )}
              </div>

              {/* Messages */}
              <div className="h-[420px] overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
                {messages.map((msg, i) => (
                  <MessageBubble key={i} msg={msg} />
                ))}
                {sending && (
                  <div className="flex gap-2.5">
                    <div className="h-7 w-7 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <Bot size={14} className="text-emerald-400" />
                    </div>
                    <div className="bg-muted/80 rounded-xl rounded-tl-sm px-3.5 py-2.5 border border-border">
                      <Loader2 size={14} className="animate-spin text-emerald-400" />
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Input (only for active sessions) */}
              {isActive && (
                <div className="border-t border-border px-4 py-3">
                  <div className="flex gap-2">
                    <input
                      ref={inputRef}
                      type="text"
                      value={input}
                      onChange={e => setInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                      placeholder="Type a message as the caller..."
                      className="flex-1 h-10 rounded-lg bg-muted/80 border border-border text-foreground text-sm px-3.5 placeholder:text-muted-foreground focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20 transition-colors"
                      disabled={sending}
                      data-testid="chat-input"
                    />
                    <Button onClick={sendMessage} disabled={!input.trim() || sending}
                      className="bg-emerald-500 hover:bg-emerald-600 text-white h-10 px-4"
                      data-testid="send-message-btn">
                      {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    </Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1.5">
                    Tip: Type as if you're the caller. The AI responds using the same logic as live calls.
                  </p>
                </div>
              )}
            </Card>
          )}

          {/* Scorecard Display (after ending or from review) */}
          {scorecard && !isActive && (
            <div className="mt-4">
              <Scorecard scorecard={scorecard} onClose={isReview ? null : resetToSetup} />
              <UpgradeNudge text="Unlock premium voice for higher caller engagement and conversion rates" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
